// ═══════════════════════════════════════════════════════════════════════════
// Canjear un ticket
//
// Aparece cuando alguien tiene sesión válida pero NO pertenece a ninguna
// vinícola. Antes de esto, el guardia lo sacaba: "la cuenta perdió acceso".
// Correcto cuando a alguien le quitaron el acceso, pero es exactamente el
// estado en el que llega un vendedor nuevo — y sacarlo lo dejaba sin manera
// de entrar nunca.
//
// También sirve al vendedor que YA trabaja con una vinícola y recibe un ticket
// de otra: desde aquí lo canjea sin cerrar sesión.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef } from "react";
import { consumirInvitacion } from "../datos/vendedores.js";
import { salir } from "./sesion.js";
import { olvidarTicket } from "./ticket.js";
import { LOGO_BASE64 } from "../pdf/logo.js";

export function CanjearTicket({ sesion, alEntrar, alCancelar, codigoInicial, yaTeniaAcceso }) {
  const [codigo, setCodigo] = useState(codigoInicial || "");
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState("");
  const [listo, setListo] = useState(null);

  const canjear = async (forzado) => {
    const c = (forzado || codigo).trim().toUpperCase();
    if (!c) return;
    setOcupado(true); setError("");
    try {
      const r = await consumirInvitacion(c);
      olvidarTicket();          // ya se usó: no debe reaparecer
      setListo(r);
      // Un instante para que se lea a qué vinícola entró, y adentro.
      setTimeout(() => alEntrar?.(r), 1400);
    } catch (e) {
      setError(e.message);
      setOcupado(false);
    }
  };

  // Si el código vino en la liga, se canjea solo: el usuario ya hizo clic una
  // vez al abrirla; pedirle que además le dé a un botón no aporta nada.
  const yaIntentado = useRef(false);
  useEffect(() => {
    if (codigoInicial && !yaIntentado.current) {
      yaIntentado.current = true;
      canjear(codigoInicial);
    }
  }, [codigoInicial, canjear]);

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center",
                  justifyContent: "center", background: "#0a0a0a", padding: 20 }}>
      <div style={{ maxWidth: 420, width: "100%" }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <img src={LOGO_BASE64} alt="" style={{ width: 64, height: 64, objectFit: "contain" }} />
        </div>

        {listo ? (
          <div style={{ background: "#101408", border: "1px solid #2a3a1a", borderRadius: 12,
                        padding: 28, textAlign: "center" }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>✓</div>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22,
                          color: "#c9d8a0", marginBottom: 6 }}>
              {listo.tenant}
            </div>
            <div style={{ fontSize: 13, color: "#7a8a5a" }}>
              {listo.membresia_nueva ? "Ya tienes acceso" : "Ya tenías acceso a esta vinícola"}
            </div>
          </div>
        ) : (
          <div style={{ background: "#111", border: "1px solid #222", borderRadius: 12, padding: 28 }}>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24,
                          color: "#c9a96e", marginBottom: 8 }}>
              Tienes un ticket
            </div>
            <div style={{ fontSize: 13, color: "#888", lineHeight: 1.7, marginBottom: 20 }}>
              {yaTeniaAcceso
                ? <>Traes una invitación para otra vinícola. Al canjearla podrás cambiar
                    entre las dos con la misma cuenta.</>
                : <>Tu cuenta <b style={{ color: "#aaa" }}>{sesion?.user?.email}</b> todavía
                    no pertenece a ninguna vinícola. Escribe el código que te dieron.</>}
            </div>

            <input
              value={codigo}
              onChange={e => setCodigo(e.target.value.toUpperCase())}
              onKeyDown={e => e.key === "Enter" && canjear()}
              placeholder="XXXXX-XXXXX"
              autoFocus
              autoCapitalize="characters"
              spellCheck={false}
              style={{ width: "100%", padding: "14px 16px", fontSize: 22, textAlign: "center",
                       fontFamily: "monospace", letterSpacing: 3, background: "#0a0a0a",
                       border: "1px solid #2a2a2a", borderRadius: 8, color: "#c9a96e",
                       marginBottom: 14, boxSizing: "border-box" }} />

            {error && (
              <div style={{ padding: "10px 12px", background: "#1f0d0d", border: "1px solid #3a1a1a",
                            borderRadius: 6, fontSize: 12, color: "#f87171", marginBottom: 14 }}>
                {error}
              </div>
            )}

            <button onClick={() => canjear()} disabled={ocupado || !codigo.trim()}
              style={{ width: "100%", padding: "13px", fontSize: 14, fontWeight: 600,
                       borderRadius: 8, border: "1px solid #3a3a1a", cursor: "pointer",
                       background: "linear-gradient(135deg, #2a2a1a, #1a1a0d)", color: "#c9a96e",
                       opacity: (ocupado || !codigo.trim()) ? 0.4 : 1 }}>
              {ocupado ? "⏳ Entrando…" : "Entrar"}
            </button>

            <div style={{ fontSize: 11, color: "#555", lineHeight: 1.7, marginTop: 16 }}>
              El ticket sirve una sola vez y vence. Si no funciona, pídele otro a quien
              te invitó.
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 20, paddingTop: 16,
                          borderTop: "1px solid #1a1a1a" }}>
              {alCancelar && (
                <button onClick={alCancelar}
                  style={{ flex: 1, padding: "9px", fontSize: 12, borderRadius: 6,
                           border: "1px solid #222", background: "transparent",
                           color: "#888", cursor: "pointer" }}>
                  Volver
                </button>
              )}
              <button onClick={() => salir()}
                style={{ flex: 1, padding: "9px", fontSize: 12, borderRadius: 6,
                         border: "1px solid #222", background: "transparent",
                         color: "#888", cursor: "pointer" }}>
                Cerrar sesión
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
